import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import {
  ArrowRightLeft,
  Check,
  ChevronRight,
  Info,
  Pencil,
  Plus,
  Save,
  Trash,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type CurrencyObj = { code: string; symbol?: string; [k: string]: any };

function normalizeCollectionLike(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const values = Object.values(value);
  if (!values.length) return [];
  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object"
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function readCodeFromCurrencyLike(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    return raw.toUpperCase();
  }
  if (typeof value === "object") {
    const raw = String(value?.code ?? value?.currencyCode ?? "").trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    return raw.toUpperCase();
  }
  return null;
}

function readIdFromCurrencyLike(value: any): number | null {
  const raw =
    typeof value === "object"
      ? value?.id
      : typeof value === "number" || typeof value === "string"
      ? value
      : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(
  funding: any,
  projectCurrency?: CurrencyObj | null
): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const projectCurrencyId = readIdFromCurrencyLike(projectCurrency);
  const projectCurrencyCode = readCodeFromCurrencyLike(projectCurrency);
  if (
    directId &&
    projectCurrencyId &&
    directId === projectCurrencyId &&
    projectCurrencyCode
  ) {
    return projectCurrencyCode;
  }

  return null;
}

function isFundingInputInLocalCurrency(
  funding: any,
  projectCurrency?: CurrencyObj | null
): boolean {
  const code = readFundingInputCurrencyCode(funding, projectCurrency);
  return !!code && code !== "EUR";
}

const VersementCard = ({
  currency, // objet devise non-EUR
  currencySelected, // string | null -> code devise sélectionnée; null => EUR
  desiredFundings, // laissé tel quel même si non utilisé, pour compat compat
  funding, // doit contenir .id et .realFundingExpectedPaids
  onChange,
  readonly = false,
}: {
  currency: CurrencyObj | null;
  currencySelected: string | null;
  desiredFundings?: any;
  funding: any;
  onChange: (rows: any[]) => void;
  readonly?: boolean;
}) => {
  // Normalisation initiale
  const [localVersements, setLocalVersements] = useState(
    (funding?.realFundingExpectedPaids || []).map((v: any) => ({
      ...v,
      expectedAt: v.at ? String(v.at).slice(0, 10) : null,
      at: v.datePaid ? String(v.datePaid).slice(0, 10) : "",
      status: v.status === 0 ? 1 : v.status ?? 1,
      paymentSlip: v.paymentSlip || { id: null, name: "", uri: "#" },
      // champs FX éventuels (si renvoyés par le back)
      amountConverted: v.amountConverted ?? v.amount_converted ?? null,
      oldRate: v.oldRate ?? v.old_rate ?? null,
      dateOldRate:
        v.dateOldRate || v.date_old_rate
          ? String(v.dateOldRate || v.date_old_rate).slice(0, 10)
          : null,
    }))
  );

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [originalVersement, setOriginalVersement] = useState<any>(null);
  const [openDetailsIndex, setOpenDetailsIndex] = useState<number | null>(null); // panneau (date virement + PJ)

  // on mémorise l'ancien code devise (string | null)
  const previousCurrencyRef = useRef<string | null>(currencySelected);
  const { postData } = usePostData();

  const { mutate: saveData } = useMutateData({
    method: "POST",
    id: "verdeo-fundings-project",
    onSuccess: () => toast("Les données ont été bien mises à jour."),
  });

  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();

  // Helpers devise sélectionnée
  const isFXSelected = currencySelected === (currency?.code || "");
  const currencySymbol = isFXSelected
    ? currency?.symbol || currency?.code || ""
    : "€";
  const fundingInputIsLocalCurrency = useMemo(
    () => isFundingInputInLocalCurrency(funding, currency),
    [funding, currency]
  );

  // Conversion visuelle montants selon devise sélectionnée (on convertit v.amount)
  useEffect(() => {
    if (readonly) {
      previousCurrencyRef.current = currencySelected;
      return;
    }
    const convertVersements = async () => {
      const prevCode = previousCurrencyRef.current; // string | null
      const nowCode = currencySelected; // string | null
      const fxCode = currency?.code || "";

      // EUR -> FX
      if (prevCode === null && nowCode === fxCode) {
        const converted = await Promise.all(
          localVersements.map(async (v: any) => ({
            ...v,
            amount: await convertEuroToCurrency(v.amount, currency),
          }))
        );
        setLocalVersements(converted);
      }
      // FX -> EUR
      else if (prevCode === fxCode && nowCode === null) {
        const converted = await Promise.all(
          localVersements.map(async (v: any) => ({
            ...v,
            amount: await convertToEuro(v.amount, currency),
          }))
        );
        setLocalVersements(converted);
      }
      // autres cas -> rien (inchangé)

      previousCurrencyRef.current = nowCode;
    };
    convertVersements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencySelected, currency?.code, readonly]);

  // ===== TRI D’AFFICHAGE PAR expectedAt (ASC) =====
  const displayVersements = useMemo(() => {
    const ts = (d?: string | null) => {
      if (!d) return Number.POSITIVE_INFINITY; // sans date prévue -> fin
      const t = Date.parse(d);
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    return [...localVersements].sort((a, b) => {
      const da = ts(a.expectedAt);
      const db = ts(b.expectedAt);
      if (da !== db) return da - db;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [localVersements]);

  // Upload PJ (compress si image, puis envoie)
  const uploadMedia = async (baseIndex: number, file: File) => {
    if (readonly) return;
    const data = new FormData();
    data.append("type", "12");

    const fileDefaultName = file.name;
    let compressedBlob: Blob | null = null;
    try {
      compressedBlob = await handleCompressImage(file, 1024, 768, 0.9);
    } catch {
      compressedBlob = null;
    }

    if (compressedBlob) {
      const renamedFile = new File([compressedBlob], fileDefaultName, {
        type: compressedBlob.type,
      });
      data.append("files[]", renamedFile);
    } else {
      data.append("files[]", file);
    }

    const newMedias: any = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data,
    });

    if (!Array.isArray(newMedias)) {
      toast(newMedias?.message || "Erreur lors de l'upload");
      return;
    }

    const updated = [...localVersements];
    updated[baseIndex].paymentSlip = newMedias[0];
    setLocalVersements(updated);
  };

  // Changement local simple
  const handleChange = (baseIndex: number, key: string, value: any) => {
    if (readonly) return;
    const updated = [...localVersements];
    switch (key) {
      case "amount":
        updated[baseIndex].amount =
          typeof value === "number"
            ? value
            : parseFloat(String(value).replace(",", ".")) || 0;
        break;
      case "expectedAt":
        updated[baseIndex].expectedAt = String(value ?? "");
        break;
      case "status": {
        const prevReceived = updated[baseIndex].status === 2;
        let v = Number(value);
        if (v === 0) v = 1; // 0 -> 1 (sécurité legacy)
        updated[baseIndex].status = v;
        const nowReceived = v === 2;
        if (!prevReceived && nowReceived) setOpenDetailsIndex(baseIndex);
        else if (prevReceived && !nowReceived) setOpenDetailsIndex(null);
        break;
      }
      case "at":
        updated[baseIndex].at = String(value ?? "");
        break;
      default:
        updated[baseIndex][key] = value;
    }
    setLocalVersements(updated);
  };

  // SAVE
  const handleSave = async (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    const v = updated[baseIndex];

    if (!v.expectedAt) {
      toast("Veuillez renseigner la date prévue.");
      return;
    }
    const statusNormalized = v.status === 0 ? 1 : v.status;
    if (statusNormalized === 2) {
      if (!v.at) {
        toast("Veuillez renseigner la date du virement.");
        setOpenDetailsIndex(baseIndex);
        return;
      }
      if (!v.paymentSlip?.id) {
        toast("Veuillez ajouter une pièce jointe (bordereau).");
        setOpenDetailsIndex(baseIndex);
        return;
      }
    }

    // Envoi en EUR côté back si la devise non-EUR est sélectionnée
    const amountInEur = isFXSelected
      ? await convertToEuro(v.amount, currency)
      : v.amount;

    const payload = {
      realfunding: funding.id,
      amount: amountInEur,
      expectedAt: v.expectedAt,
      status: statusNormalized,
      ...(statusNormalized === 2 ? { at: v.at } : {}),
      ...(statusNormalized === 2 && v.paymentSlip?.id
        ? { paymentSlip: v.paymentSlip.id }
        : {}),
      ...(String(v.id).startsWith("tmp-") ? {} : { toEdit: v.id }),
    };

    try {
      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding-expected-paid/save`,
        data: payload,
      });

      if (typeof res?.id !== "undefined") updated[baseIndex].id = res.id;

      setLocalVersements(updated);
      setEditIndex(null);
      setOriginalVersement(null);
      setOpenDetailsIndex(null);
      onChange(updated);

      toast("Le versement a été enregistré.");
    } catch (err) {
      console.error(err);
      toast("Erreur lors de l’enregistrement du versement.");
    }
  };

  // DELETE
  const handleDelete = async (baseIndex: number) => {
    if (readonly) return;
    const confirmDelete = window.confirm(
      "Êtes-vous sûr de vouloir supprimer ce versement ?"
    );
    if (!confirmDelete) return;

    const updated = [...localVersements];
    const toDelete = updated[baseIndex];

    try {
      if (toDelete.id && !String(toDelete.id).startsWith("tmp-")) {
        await saveData({
          endpoint: `${
            import.meta.env.VITE_IP_API
          }/api/real-funding-expected-paid/delete/${toDelete.id}`,
          body: {},
        });
      }
      updated.splice(baseIndex, 1);
      setLocalVersements(updated);
      onChange(updated);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
    }
  };

  const handleCancel = (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    if (originalVersement) {
      updated[baseIndex] = originalVersement;
    } else {
      updated.splice(baseIndex, 1);
    }
    setLocalVersements(updated);
    setEditIndex(null);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  const addVersement = async () => {
    if (readonly) return;
    if (editIndex !== null) {
      toast("Termine l’édition en cours avant d’ajouter un versement.");
      return;
    }

    const newVersement = {
      id: `tmp-${Date.now()}`,
      amount: 0,
      expectedAt: new Date().toISOString().slice(0, 10), // Date prévue par défaut
      status: 1, // Prévu
      at: "", // rempli uniquement si Reçu
      paymentSlip: { id: null, name: "", uri: "#" },
      amountConverted: null,
      oldRate: null,
      dateOldRate: null,
    };

    const updated = [...localVersements, newVersement];
    setLocalVersements(updated);
    setEditIndex(updated.length - 1);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  // Switch “Reçu ?”
  const ReceivedSwitch = ({
    checked,
    onToggle,
  }: {
    checked: boolean;
    onToggle: () => void;
  }) => (
    <button
      type="button"
      onClick={() => {
        if (!readonly) onToggle();
      }}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? "bg-green-600" : "bg-gray-300"
      } ${readonly ? "opacity-50 cursor-not-allowed" : ""}`}
      aria-pressed={checked}
      aria-label="Reçu ?"
      title={checked ? "Reçu : Oui" : "Reçu : Non"}
      disabled={readonly}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
          checked ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  );

  // === FX Info (popover desktop à côté du label + cartouche mobile sous le label) ===
  const RateInfo = ({ v }: { v: any }) => {
    if (v.status !== 2 || !v.oldRate || !v.dateOldRate) return null;
    const d = new Date(v.dateOldRate);
    const dateStr = isNaN(d.getTime())
      ? v.dateOldRate
      : d.toLocaleDateString("fr-FR");
    const targetSymbol = isFXSelected
      ? currency?.symbol || currency?.code || ""
      : "€";
    const amountConvertedDisplay =
      v.amountConverted != null
        ? Number(v.amountConverted)
        : !fundingInputIsLocalCurrency && Number(v.oldRate) > 0
        ? Number(v.amount || 0) / Number(v.oldRate)
        : null;

    return (
      <>
        {/* Desktop: icône popover à côté du label */}
        <div className="hidden sm:inline-flex">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
                aria-label="Voir le taux utilisé"
                title="Voir le taux utilisé"
              >
                <Info size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              className="text-xs w-[320px] max-w-[90vw] p-3"
            >
              <div className="space-y-1">
                <div className="font-medium">Taux utilisé</div>
                <div>
                  1€ ={" "}
                  <strong>
                    {(1 / Number(v.oldRate)).toLocaleString("fr-FR")}{" "}
                    {targetSymbol}
                  </strong>
                </div>
                <div>
                  au <strong>{dateStr}</strong>
                </div>
                {amountConvertedDisplay != null && (
                  <div className="mt-1 text-[11px] text-gray-600">
                    Montant converti:{" "}
                    <strong>
                      {Number(amountConvertedDisplay).toLocaleString("fr-FR")}{" "}
                      {targetSymbol}
                    </strong>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile: cartouche sous le label */}
        <div className="sm:hidden mt-1 text-[11px] bg-gray-100 border border-gray-200 rounded px-2 py-1 text-gray-700">
          <div className="font-medium mb-0.5">Taux utilisé</div>
          <div>
            1€ ={" "}
            <strong>
              {(1 / Number(v.oldRate)).toLocaleString("fr-FR")} {targetSymbol}
            </strong>
          </div>
          <div>
            au <strong>{dateStr}</strong>
          </div>
          {amountConvertedDisplay != null && (
            <div className="mt-0.5">
              Montant converti:{" "}
              <strong>
                {Number(amountConvertedDisplay).toLocaleString("fr-FR")}{" "}
                {targetSymbol}
              </strong>
            </div>
          )}
        </div>
      </>
    );
  };

  // Montant affiché : en lecture seule, applique la même règle que le suivi:
  // - non-Verdeo saisi en devise locale => FX = amountConverted
  // - non-Verdeo saisi en EUR => FX = amount / oldRate (taux contrat)
  const displayAmount = (v: any) => {
    if (!readonly) {
      if (isFXSelected) {
        if (v.status === 2 && v.amountConverted != null)
          return Number(v.amountConverted);
        return Number(v.amount);
      }
      return Number(v.amount);
    }

    const amountEuro = Number(v.amount || 0);
    if (!isFXSelected) return amountEuro;
    if (v.status !== 2) return amountEuro;

    if (fundingInputIsLocalCurrency) {
      if (v.amountConverted != null) return Number(v.amountConverted);
      return amountEuro;
    }

    if (v.oldRate && Number(v.oldRate) > 0) {
      return amountEuro / Number(v.oldRate);
    }
    if (v.amountConverted != null) return Number(v.amountConverted);
    return amountEuro;
  };

  return (
    <div className="mt-4">
      {/* Header + bouton d’ajout */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-gray-900 font-medium flex items-center gap-2">
          <ArrowRightLeft size={18} className="" />
          Versements reçus
        </h2>
        {!readonly && (
          <button
            onClick={addVersement}
            className="text-sm text-green-600 hover:underline inline-flex items-center gap-1"
          >
            <Plus size={16} /> Ajouter un versement
          </button>
        )}
      </div>

      {displayVersements.map((v: any) => {
        // On remappe chaque ligne triée vers son index réel dans localVersements
        const baseIndex = localVersements.findIndex((x: any) => x.id === v.id);
        const isReceived = v.status === 2;
        const detailsOpen = openDetailsIndex === baseIndex;
        const amountForDisplay = displayAmount(v);

        return (
          <div key={v.id} className="bg-gray-50 p-3 rounded mb-2">
            {!readonly && editIndex === baseIndex ? (
              <>
                {/* Ligne compacte (mobile-friendly) */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start">
                  {/* Montant */}
                  <div className="relative">
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-gray-600">Montant</label>
                      {isFXSelected && <RateInfo v={v} />}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={Number.isFinite(v.amount) ? v.amount : 0}
                      onChange={(e) =>
                        handleChange(baseIndex, "amount", e.target.value)
                      }
                      className="mt-1 input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                    <span className="absolute right-2 top-[46px] -translate-y-1/2 text-sm text-gray-600">
                      {currencySymbol}
                    </span>
                  </div>

                  {/* Date prévue */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Date prévue
                    </label>
                    <input
                      type="date"
                      value={v.expectedAt || ""}
                      onChange={(e) =>
                        handleChange(baseIndex, "expectedAt", e.target.value)
                      }
                      className="input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                  </div>

                  {/* Reçu ? */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Reçu ?
                    </label>
                    <div className="flex items-center gap-3">
                      <ReceivedSwitch
                        checked={isReceived}
                        onToggle={() =>
                          handleChange(baseIndex, "status", isReceived ? 1 : 2)
                        }
                      />
                      {isReceived ? (
                        <span className="text-green-700 text-sm inline-flex items-center gap-1">
                          <Check size={16} /> Oui
                        </span>
                      ) : (
                        <span className="text-gray-600 text-sm inline-flex items-center gap-1">
                          <X size={16} /> Non
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-end gap-2 justify-end">
                    <button
                      onClick={() => handleSave(baseIndex)}
                      className="text-green-600 text-sm"
                      title="Enregistrer"
                      disabled={readonly}
                    >
                      <Save size={16} />
                    </button>
                    <button
                      onClick={() => handleCancel(baseIndex)}
                      className="text-gray-600 text-sm"
                      title="Annuler"
                      disabled={readonly}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Panneau repliable : Détails reçu (date virement + PJ) */}
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={!isReceived}
                    onClick={() =>
                      setOpenDetailsIndex(detailsOpen ? null : baseIndex)
                    }
                    className={`flex items-center gap-1 text-sm underline ${
                      isReceived
                        ? "text-blue-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Détails reçu (date du virement & pièce jointe)
                    {isReceived && (
                      <ChevronRight
                        size={16}
                        className={"ml-1 " + (detailsOpen ? "rotate-90" : "")}
                      />
                    )}
                  </button>

                  {isReceived && detailsOpen && (
                    <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-3">
                      {/* Date du virement */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Date du virement
                        </label>
                        <input
                          type="date"
                          value={v.at || ""}
                          onChange={(e) =>
                            handleChange(baseIndex, "at", e.target.value)
                          }
                          className="input w-full border rounded px-2 py-1"
                          disabled={readonly}
                        />
                      </div>

                      {/* PJ */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Pièce jointe (bordereau)
                        </label>
                        {v.paymentSlip?.id ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={
                                import.meta.env.VITE_MEDIA_BASE +
                                v.paymentSlip.uri
                              }
                              target="_blank"
                              className="underline text-sm"
                              rel="noreferrer"
                            >
                              📄 {v.paymentSlip.name}
                            </a>
                            {!readonly && (
                              <label className="ml-2 inline-flex items-center gap-1 text-xs cursor-pointer text-blue-700">
                                <Upload size={14} />
                                <span>Changer</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0])
                                      uploadMedia(baseIndex, e.target.files[0]);
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        ) : (
                          !readonly && (
                            <label className="inline-flex items-center gap-1 text-sm cursor-pointer text-blue-700">
                              <Upload size={16} />
                              <span>Ajouter une pièce jointe</span>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files?.[0])
                                    uploadMedia(baseIndex, e.target.files[0]);
                                }}
                              />
                            </label>
                          )
                        )}
                      </div>

                      <p className="text-xs text-gray-500">
                        Astuce : sur petit écran, renseigne Montant & Date
                        prévue, puis ouvre “Détails reçu”.
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* LECTURE / LISTING compact */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                  {/* Montant en 2 lignes: label + (popover/cartouche) puis valeur en dessous */}
                  <div>
                    <div className="flex items-center gap-1">
                      <strong>Montant</strong>
                      {isFXSelected && <RateInfo v={v} />}
                    </div>
                    <div className="mt-1">
                      {Math.trunc(amountForDisplay).toLocaleString()}{" "}
                      {currencySymbol}
                    </div>
                  </div>

                  <div>
                    <strong>Date prévue :</strong>{" "}
                    {v.expectedAt
                      ? new Date(v.expectedAt).toLocaleDateString("fr-FR")
                      : "—"}
                  </div>

                  <div className="flex items-center gap-2">
                    <strong>Reçu ?</strong>
                    {isReceived ? (
                      <Check
                        size={18}
                        className="text-green-600"
                        aria-label="Reçu"
                      />
                    ) : (
                      <X
                        size={18}
                        className="text-red-600"
                        aria-label="Non reçu"
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-2 justify-end">
                    {!readonly && (
                      <>
                        <button
                          onClick={() => {
                            setOriginalVersement({
                              ...localVersements[baseIndex],
                            });
                            setEditIndex(baseIndex);
                            setOpenDetailsIndex(null);
                          }}
                          className="text-sm"
                          title="Modifier"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(baseIndex)}
                          className="text-red-500 text-sm"
                          title="Supprimer"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Panneau repliable : Détails reçu (read-only) */}
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={!isReceived}
                    onClick={() =>
                      setOpenDetailsIndex(detailsOpen ? null : baseIndex)
                    }
                    className={`flex items-center gap-1 text-sm underline ${
                      isReceived
                        ? "text-blue-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Détails reçu (date du virement & pièce jointe)
                    {isReceived && (
                      <ChevronRight
                        size={16}
                        className={"ml-1 " + (detailsOpen ? "rotate-90" : "")}
                      />
                    )}
                  </button>

                  {isReceived && detailsOpen && (
                    <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-2">
                      <div>
                        <strong>Date du virement :</strong>{" "}
                        {v.at
                          ? new Date(v.at).toLocaleDateString("fr-FR")
                          : "—"}
                      </div>
                      <div>
                        <strong>PJ :</strong>{" "}
                        {v.paymentSlip?.id ? (
                          <a
                            href={
                              import.meta.env.VITE_MEDIA_BASE +
                              v.paymentSlip.uri
                            }
                            target="_blank"
                            className="underline"
                            rel="noreferrer"
                          >
                            {v.paymentSlip.name}
                          </a>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VersementCard;
